import { useCallback, useMemo, useRef } from 'react';
import { useMasterTable } from '@/features/masters';
import type { UseMasterTableResult } from '@/features/masters';
import { createTemplateApi } from '../../overview/api/templateApi';
import type { TemplateDefinition } from '../../overview/models';
import type { TemplatePayload, TemplateRecord } from '../../overview/models/template.types';
import type { HierarchyScope } from '../../overview/models/hierarchyScope';
import { EMPTY_SCOPE, scopeQuery } from '../../overview/models/hierarchyScope';

export interface UseTemplateListResult extends UseMasterTableResult<TemplateRecord, TemplatePayload> {
  /**
   * The Template Code the server gave the last template this screen created, or null. Read straight after an
   * awaited `createRow`, so the Draft Saved popup can name it (2026-10-14).
   */
  lastCreatedCode: () => string | null;
}

/**
 * The list screen's state.
 *
 * A thin wrapper over the masters engine, which already does every one of this
 * screen's list behaviours -- debounced search, status, filter conditions,
 * paging, sorting, the CRUD calls, `saving`, `error`, and a detail read. None of
 * it needed changing for a fourth status, because the hook carries `status` as a
 * plain string and the option list is the control's business.
 *
 * Reusing it rather than extending `masters/common`'s presentation is deliberate:
 * the hook is the part that genuinely generalises, and the eighteen master
 * screens rendering through those components are not worth the risk of a shared
 * change this feature could make in its own folder. That is also why the created
 * template's code is caught here, around `create`, rather than returned by the
 * engine's `createRow`.
 */
export function useTemplateList(
  definition: TemplateDefinition,
  /** The list's hierarchy filter, on a kind that collects one; the server applies it. */
  scope: HierarchyScope = EMPTY_SCOPE,
): UseTemplateListResult {
  const createdCode = useRef<string | null>(null);
  const crud = useMemo(() => {
    const api = createTemplateApi(definition.kind);
    return {
      ...api,
      create: async (payload: TemplatePayload) => {
        createdCode.current = null;
        const created = await api.create(payload);
        createdCode.current = created.templateCode?.trim() || null;
        return created;
      },
    };
  }, [definition.kind]);
  const table = useMasterTable<TemplateRecord, TemplatePayload>({
    crud,
    pageSize: 25,
    extraQuery: definition.collectsHierarchy ? scopeQuery(scope) : undefined,
  });
  const lastCreatedCode = useCallback(() => createdCode.current, []);
  return { ...table, lastCreatedCode };
}
